import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type { AxiosInstance } from 'axios';
import { isAppError, toAppError, type AppError } from '../errors/AppError';
import {
  AuthEnvelopeSchema,
  ForgotPasswordRequestSchema,
  MeResponseSchema,
  OrganizationsListResponseSchema,
  SendEmailVerificationResponseSchema,
  type OrganizationListItem,
  type SendEmailVerificationResponse,
  type User,
} from '../dtos/wire';
import { useHttpClient } from './HttpApiProvider';
import { memorySessionStorage, type SessionStorage } from './SessionStorage';

/**
 * Backoff schedule (in ms) for the bootstrap probes. Picked to ride
 * through the typical "I just restarted the dev server" outage
 * (~2-4s) without giving up; production tolerates a brief network
 * blip with the same logic.
 *
 * Total worst-case wait: 500 + 1500 + 4500 ≈ 6.5s before we surrender
 * and either keep the cached identity (if we had one) or fall back
 * to anonymous.
 */
const BOOTSTRAP_BACKOFF_MS = [500, 1500, 4500] as const;

/**
 * Cached identity record persisted on-device for fast first paint.
 *
 * SuperTokens owns the bearer access + refresh tokens internally, so
 * this struct only carries the small surface we need before the SDK
 * has hydrated: who the user is and a display name. `token` is
 * populated only immediately after a sign-in / sign-up where the
 * server echoes it in the response body.
 */
export interface Session {
  userId: string;
  email: string;
  displayName: string;
  /** Opaque access token echoed in sign-in / sign-up bodies. */
  token?: string;
  /** ISO timestamp when the token stops being valid. */
  expiresAt?: string;
}

export interface SignUpPayload {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
}

export interface SessionState {
  status: 'loading' | 'authenticated' | 'anonymous' | 'error';
  session: Session | null;
  user: User | null;
  organizations: OrganizationListItem[];
  /**
   * `true` once we have an authoritative answer about the user's org
   * membership — either a successful `/orgs` fetch (zero or more
   * items) or a definitive auth rejection (no session → `[]`).
   *
   * Distinct from `organizations.length === 0` because that condition
   * is also true while we're still loading or while a transient probe
   * failure has left the projection empty. Consumers that take
   * destructive action when the list is empty (e.g. routing to
   * "/create-business", clearing the persisted active-org id) MUST
   * gate on this flag — without it, a flaky network on app reload
   * looks identical to "user has no orgs" and we yank the user into
   * the wrong screen.
   *
   * Lifecycle:
   *   - Initial load:                 false
   *   - Probe ok (any item count):    true
   *   - Probe rejected (auth=401):    true (cleared session, no orgs)
   *   - Probe transient error:        STAYS false until the next
   *                                   bootstrap / sign-in succeeds.
   */
  organizationsLoaded: boolean;
  error: Error | null;
  /**
   * `null` while we don't yet know (loading / anonymous / API not
   * surfacing the field), `true` / `false` once `/auth/me` has
   * answered. UI should treat `null` the same as `true` — never
   * nag a user about verification when we don't know the state.
   */
  emailVerified: boolean | null;
  refresh: () => Promise<void>;
  /**
   * Re-poll `/auth/me` without redoing the whole bootstrap. Cheap,
   * idempotent — used by mobile after the user taps "I verified my
   * email" so the banner clears without a sign-out / sign-in cycle.
   */
  refreshEmailVerified: () => Promise<void>;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (payload: SignUpPayload) => Promise<void>;
  signOut: () => Promise<void>;
  /**
   * `POST /v1/auth/forgot-password`. Always resolves (server returns
   * 204 even for unknown emails to avoid enumeration); rejects only
   * on schema violation or transport error. Mobile shows a "check
   * your email" success screen on resolve regardless of whether the
   * email is registered.
   */
  forgotPassword: (email: string) => Promise<void>;
  /**
   * `POST /v1/auth/send-email-verification`. Returns whether the
   * user was already verified so the caller can skip the success
   * sheet ("Already verified.") instead of showing "Email sent."
   */
  resendEmailVerification: () => Promise<SendEmailVerificationResponse>;
  /**
   * Replace the cached organizations projection in the session.
   *
   * The session keeps its own copy of the org list (separate from the
   * React Query cache) so the shell can render `useActiveOrganization()`
   * before any query hook subscribes. Mutations that change membership
   * (create / archive / restore / hard-delete / update) must call this
   * after they patch the React Query cache so the two stay in lockstep.
   *
   * Without this, `setActiveOrganization(newOrgId)` followed by the
   * active-org guard would see "id not in session list" and immediately
   * snap back to `organizations[0]` — i.e. the user can't switch into a
   * freshly-created workspace until the next bootstrap.
   */
  syncOrganizations: (items: OrganizationListItem[]) => void;
}

const SessionContext = createContext<SessionState | null>(null);

/**
 * Pluggable shim around the SuperTokens session SDK.
 *
 * Mobile (`supertokens-react-native`) and web (`supertokens-auth-react`
 * / `supertokens-website`) export the same shape on different module
 * paths. The provider only ever needs two operations — "is there a
 * session?" and "sign out locally". By accepting both as a prop the
 * package stays SDK-agnostic; tests and storybook can also pass a
 * stub without touching either SDK.
 *
 * If the prop is omitted the provider falls back to its built-in
 * `require('supertokens-react-native')` discovery (preserving the
 * existing mobile contract) and returns `false` / no-op on web,
 * which is the right behaviour when the host hasn't wired the web
 * SDK yet.
 */
export interface SessionAuthAdapter {
  doesSessionExist: () => Promise<boolean>;
  signOut: () => Promise<void>;
}

export interface SessionProviderProps {
  children: ReactNode;
  /**
   * Optional seed used by tests / storybook to render an authenticated
   * shell without round-tripping through the network. In production
   * code this stays `null` and the provider does the boot itself.
   */
  initialSession?: Session | null;
  /**
   * Pluggable session storage. Mobile wires this to SecureStore for
   * cached identity (so first paint can render the user's name before
   * `/users/me` resolves). The bearer access + refresh tokens are NOT
   * stored here — `supertokens-react-native` owns those internally.
   *
   * Defaults to an in-memory store, which is appropriate for tests and
   * temporary sessions but never for shipping mobile builds.
   */
  storage?: SessionStorage;
  /**
   * Optional session adapter for the active SuperTokens SDK. Mobile
   * leaves this undefined (the legacy `require('supertokens-react-native')`
   * discovery still works); web injects the `supertokens-auth-react`
   * Session module wrapped in this shape.
   */
  auth?: SessionAuthAdapter;
}

/**
 * Owns the user's session lifecycle.
 *
 * Responsibilities:
 *   1. Boot — read the cached identity from `storage` for instant
 *      first paint, then ask `supertokens-react-native` whether a
 *      session exists. If so, hydrate from `/v1/auth/me` and
 *      `/v1/orgs`.
 *   2. Sign-in / sign-up — POST to the corresponding `/v1/auth/...`
 *      route. The SuperTokens SDK reads `st-access-token` /
 *      `st-refresh-token` response headers and stashes them.
 *      Subsequent axios calls get the bearer header automatically via
 *      `addAxiosInterceptors`.
 *   3. Sign-out — call `/v1/auth/sign-out` (server revokes the
 *      session) and `SuperTokens.signOut()` (SDK clears the local
 *      tokens). Wiping the cached identity in `storage` is also
 *      handled here.
 *
 * The provider is intentionally *thin*: it doesn't do any role / RBAC
 * computation, doesn't talk to React Query, doesn't know about
 * specific resources. Hooks and screens compose on top of it.
 */
export function SessionProvider({
  children,
  initialSession = null,
  storage,
  auth,
}: SessionProviderProps) {
  const http = useHttpClient();
  const storageRef = useRef<SessionStorage>(storage ?? memorySessionStorage());
  const authRef = useRef<SessionAuthAdapter | null>(auth ?? null);
  const [session, setSession] = useState<Session | null>(initialSession);
  const [user, setUser] = useState<User | null>(null);
  const [organizations, setOrganizations] = useState<OrganizationListItem[]>([]);
  const [organizationsLoaded, setOrganizationsLoaded] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [status, setStatus] = useState<SessionState['status']>('loading');

  const [emailVerified, setEmailVerified] = useState<boolean | null>(null);

  const probeUser = useCallback(
    (userId: string, fallbackEmail?: string): Promise<UserProbe> =>
      probeWithBackoff(async () => {
        const response = await http.get('/auth/me');
        const parsed = MeResponseSchema.parse(response.data);
        // The verified flag lives outside the User shape (it's a
        // SuperTokens recipe state, not a profile field) but the
        // /auth/me payload carries it. Stash on the local state so
        // the context can surface it without a second probe.
        setEmailVerified(parsed.user.emailVerified ?? null);
        return {
          id: parsed.user.id ?? userId,
          email: parsed.user.email ?? fallbackEmail ?? '',
        } satisfies User;
      }),
    [http],
  );

  const probeOrganizations = useCallback(
    (): Promise<OrgsProbe> =>
      probeWithBackoff(async () => {
        const response = await http.get('/orgs');
        const parsed = OrganizationsListResponseSchema.parse(response.data);
        return parsed.items;
      }),
    [http],
  );

  const fetchOrganizations = useCallback(async (): Promise<OrganizationListItem[]> => {
    const result = await probeOrganizations();
    // Sign-in / sign-up paths only need a best-effort list — a
    // transient hiccup degrades gracefully to "no orgs yet" and the
    // active-org guard refetches on the next mount.
    return result.kind === 'ok' ? result.value : [];
  }, [probeOrganizations]);

  const bootstrap = useCallback(
    async (initial: Session | null) => {
      try {
        const persistedRaw = initial ?? (await storageRef.current.read());
        const persisted = persistedRaw ? sanitizeSession(persistedRaw) : null;
        if (persisted) {
          // Show the cached identity immediately so the shell can
          // render before the network round-trip resolves. We still
          // confirm with SuperTokens below.
          setSession(persisted);
        }

        const sdkSessionExists = await checkSuperTokensSession(authRef.current);
        if (!sdkSessionExists && !persisted) {
          setStatus('anonymous');
          return;
        }

        const [userProbe, orgsProbe] = await Promise.all([
          probeUser(persisted?.userId ?? 'unknown', persisted?.email),
          probeOrganizations(),
        ]);

        // The server explicitly rejected the session (401). Trust it
        // — clear the cache and fall back to anonymous, regardless of
        // what the SDK thinks. This is the only place we voluntarily
        // surrender a cached identity; everything else preserves it.
        if (userProbe.kind === 'auth') {
          await storageRef.current.clear();
          setSession(null);
          setUser(null);
          setOrganizations([]);
          // Authoritative: server says this session is dead, so the
          // user has no orgs *as far as this device is concerned*.
          setOrganizationsLoaded(true);
          setStatus('anonymous');
          return;
        }

        // Server probably mid-restart, network blip, etc. If we have
        // a cached identity, render the app authenticated and let
        // route-level queries refetch when the server is back.
        // Without one, defer to whatever the SDK says — if it's also
        // empty, we're anonymous; if not, surface the error so the
        // welcome screen can offer a retry instead of a silent loop.
        if (userProbe.kind === 'transient') {
          if (persisted) {
            setSession(persisted);
            setUser(null);
            // Only mark orgs as "loaded" when the orgs probe gave a
            // real answer. If it ALSO failed transiently, leave the
            // flag false so consumers don't treat the empty list as
            // "user has zero orgs" — that's how a flaky network on
            // app reload used to bounce the user to /create-business
            // and wipe their persisted active-org id (see
            // `useActiveOrganizationGuard` for the consuming side).
            if (orgsProbe.kind === 'ok') {
              setOrganizations(orgsProbe.value);
              setOrganizationsLoaded(true);
            }
            setStatus('authenticated');
            return;
          }
          if (!sdkSessionExists) {
            setStatus('anonymous');
            return;
          }
          setError(userProbe.error);
          setStatus('error');
          return;
        }

        const profile = userProbe.value;
        const orgs = orgsProbe.kind === 'ok' ? orgsProbe.value : [];
        const nextSession: Session = {
          userId: profile.id,
          email: profile.email,
          displayName: composeDisplayName(profile),
        };

        setSession(nextSession);
        setUser(profile);
        setOrganizations(orgs);
        // Same rule as the transient branch: only mark loaded when
        // the orgs probe was authoritative. A transient orgs failure
        // alongside a successful user probe still leaves us with
        // `organizations: []` and `organizationsLoaded: false`.
        if (orgsProbe.kind === 'ok') {
          setOrganizationsLoaded(true);
        }
        await storageRef.current.write(nextSession);
        setStatus('authenticated');
      } catch (err) {
        setError(toAppError(err));
        setStatus('error');
      }
    },
    [probeOrganizations, probeUser],
  );

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      if (cancelled) return;
      await bootstrap(initialSession);
    })();
    return () => {
      cancelled = true;
    };
  }, [bootstrap, initialSession]);

  const refreshEmailVerified = useCallback(async () => {
    try {
      const response = await http.get('/auth/me');
      const parsed = MeResponseSchema.parse(response.data);
      setEmailVerified(parsed.user.emailVerified ?? null);
    } catch {
      // Best-effort. The banner state stays at whatever it was; the
      // next full bootstrap will heal it. We don't surface a toast
      // because the user explicitly tapped "I verified" and an
      // error here is most often "the click hasn't propagated yet".
    }
  }, [http]);

  const value = useMemo<SessionState>(() => {
    return {
      status,
      session,
      user,
      organizations,
      organizationsLoaded,
      error,
      emailVerified,
      syncOrganizations: (items) => {
        setOrganizations(items);
        // External callers (org-mutation hooks) only invoke this
        // when they've just received an authoritative answer from
        // the server (a list mutation succeeded, or the cache they
        // mirrored has a real value). Treat the projection as
        // loaded from this point on.
        setOrganizationsLoaded(true);
      },
      refresh: async () => {
        setStatus('loading');
        await bootstrap(null);
      },
      refreshEmailVerified,
      forgotPassword: async (email) => {
        // Validate locally so a typo doesn't burn a server round-
        // trip. The server validates again — we just give the form
        // an early error.
        const parsed = ForgotPasswordRequestSchema.parse({ email });
        await http.post('/auth/forgot-password', parsed);
      },
      resendEmailVerification: async () => {
        const response = await http.post('/auth/send-email-verification', {});
        return SendEmailVerificationResponseSchema.parse(response.data);
      },
      signIn: async (email, password) => {
        setStatus('loading');
        try {
          const response = await http.post('/auth/sign-in', { email, password });
          const envelope = AuthEnvelopeSchema.parse(response.data);
          await applyAuthEnvelope({
            envelope,
            storage: storageRef.current,
            setSession,
            setUser,
            setOrganizations,
            setOrganizationsLoaded,
            setStatus,
            setEmailVerified,
            fetchOrganizations,
          });
        } catch (err) {
          setError(toAppError(err));
          setStatus(session ? 'authenticated' : 'anonymous');
          throw isAppError(err) ? err : toAppError(err);
        }
      },
      signUp: async (payload) => {
        setStatus('loading');
        try {
          const response = await http.post('/auth/sign-up', {
            email: payload.email,
            password: payload.password,
            firstName: payload.firstName,
            lastName: payload.lastName,
          });
          const envelope = AuthEnvelopeSchema.parse(response.data);
          await applyAuthEnvelope({
            envelope,
            storage: storageRef.current,
            setSession,
            setUser,
            setOrganizations,
            setOrganizationsLoaded,
            setStatus,
            setEmailVerified,
            fetchOrganizations,
            displayNameOverride: composeDisplayNameFromInput(payload),
          });
        } catch (err) {
          setError(toAppError(err));
          setStatus(session ? 'authenticated' : 'anonymous');
          throw isAppError(err) ? err : toAppError(err);
        }
      },
      signOut: async () => {
        try {
          await http.post('/auth/sign-out', {});
        } catch {
          // Server may already consider us logged out — proceed with
          // the local cleanup either way so we never trap the user in
          // a "kind of signed in" limbo.
        }
        await runSuperTokensSignOut(authRef.current);
        await storageRef.current.clear();
        setSession(null);
        setUser(null);
        setOrganizations([]);
        // Authoritative: we just signed out, so the empty list is
        // the correct state of the world. Without this, a
        // sign-in-on-the-same-screen flow would leave
        // `organizationsLoaded=true` from the previous session
        // (which is fine because the projection IS still loaded —
        // it's just empty). Setting it explicitly here keeps the
        // semantics obvious.
        setOrganizationsLoaded(true);
        setEmailVerified(null);
        setStatus('anonymous');
      },
    };
  }, [
    bootstrap,
    fetchOrganizations,
    http,
    status,
    session,
    user,
    organizations,
    organizationsLoaded,
    error,
    emailVerified,
    refreshEmailVerified,
  ]);

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionState {
  const ctx = useContext(SessionContext);
  if (!ctx) {
    throw new Error(
      'useSession() called without a <SessionProvider>. Wrap your app root with <SessionProvider>.',
    );
  }
  return ctx;
}

export function useCurrentUser(): User | null {
  return useSession().user;
}

/**
 * Convenience selector for the signed-in user's id. Queries hooks
 * scope their cache keys by user id, so this is the canonical
 * "who am I" hook and lives next to its source of truth (the
 * session) rather than being mirrored into a separate store.
 */
export function useCurrentUserId(): string | undefined {
  return useSession().user?.id;
}

export function useOrganizations(): OrganizationListItem[] {
  return useSession().organizations;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Tri-state result for a bootstrap probe.
 *
 * - `ok`        - the server answered with a value, use it.
 * - `auth`      - the server explicitly returned 401. The session is
 *                 invalid; clear local state and fall back to anonymous.
 * - `transient` - we saw a network / timeout / 5xx after exhausting
 *                 the backoff schedule. Caller should preserve any
 *                 cached identity rather than treat the user as
 *                 logged out — server might just be restarting.
 */
type Probe<T> =
  | { kind: 'ok'; value: T }
  | { kind: 'auth' }
  | { kind: 'transient'; error: AppError };

type UserProbe = Probe<User>;
type OrgsProbe = Probe<OrganizationListItem[]>;

/**
 * Run `task` with exponential backoff, returning a `Probe` so callers
 * can distinguish "the user is signed out" from "the network blew
 * up". Only schema/auth errors are conclusive; everything else
 * (network, timeout, 5xx, unknown) is treated as transient and
 * retried until the backoff schedule is exhausted.
 *
 * Pulled out of the hook so unit tests can call it directly without
 * a React tree, and so we never accidentally surface the in-flight
 * promise on a re-render.
 */
async function probeWithBackoff<T>(
  task: () => Promise<T>,
  delays: readonly number[] = BOOTSTRAP_BACKOFF_MS,
): Promise<Probe<T>> {
  let lastError: AppError | null = null;
  for (let attempt = 0; attempt <= delays.length; attempt += 1) {
    try {
      const value = await task();
      return { kind: 'ok', value };
    } catch (err) {
      const e = toAppError(err);
      // 401 means the server actively rejected us. Anything else
      // (network, timeout, 5xx, schema parse glitch) gets one or
      // more retries before we surrender.
      if (e.kind === 'auth') return { kind: 'auth' };
      lastError = e;
      const delay = delays[attempt];
      if (delay === undefined) break;
      await sleep(delay);
    }
  }
  return {
    kind: 'transient',
    error: lastError ?? toAppError(new Error('Probe exhausted')),
  };
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

interface ApplyEnvelopeArgs {
  envelope: ReturnType<typeof AuthEnvelopeSchema.parse>;
  storage: SessionStorage;
  setSession: (next: Session) => void;
  setUser: (user: User | null) => void;
  setOrganizations: (orgs: OrganizationListItem[]) => void;
  /**
   * Marks the session's org projection as "authoritative". Sign-in /
   * sign-up always have a definitive answer (the post-auth `/orgs`
   * fetch returned, possibly empty), so callers should pass `true`.
   */
  setOrganizationsLoaded: (loaded: boolean) => void;
  setStatus: (status: SessionState['status']) => void;
  /**
   * Seeds `emailVerified` from the auth envelope so the router has
   * the right value on the very first render after sign-up /
   * sign-in. The provider already polls `/auth/me` lazily but
   * polling-after-the-fact lets the user briefly land on `(tabs)`
   * with `emailVerified === null`, which (a) skips the verify
   * gate and (b) burns 403s on every screen-level query while the
   * probe is in flight.
   */
  setEmailVerified: (next: boolean | null) => void;
  fetchOrganizations: () => Promise<OrganizationListItem[]>;
  displayNameOverride?: string;
}

async function applyAuthEnvelope(args: ApplyEnvelopeArgs): Promise<void> {
  const profile = args.envelope.user;
  const orgs = await args.fetchOrganizations();
  const nextSession: Session = {
    userId: profile.id,
    email: profile.email,
    displayName: args.displayNameOverride ?? composeDisplayName(profile),
    token: args.envelope.session.accessToken,
  };
  await args.storage.write(nextSession);
  args.setSession(nextSession);
  args.setUser(profile);
  args.setOrganizations(orgs);
  // Sign-in / sign-up always carry an authoritative answer about
  // org membership (the post-auth `/orgs` fetch returned). Mark
  // the projection loaded so downstream consumers (auth router,
  // active-org guard) can act on the empty case if it really is
  // empty.
  args.setOrganizationsLoaded(true);
  // Order matters here: we set `emailVerified` BEFORE flipping the
  // status to `authenticated`. Status drives the auth router's
  // routing decision, and the router reads `emailVerified` in the
  // same render. Setting status last guarantees the router sees a
  // consistent (status, emailVerified) pair on its first run
  // instead of the two-tick "first render: null, second render:
  // false" cadence that lets the user briefly land on `(tabs)`.
  args.setEmailVerified(profile.emailVerified ?? null);
  args.setStatus('authenticated');
}

function composeDisplayName(profile: User): string {
  return [profile.firstName, profile.lastName].filter(Boolean).join(' ').trim() || profile.email;
}

function composeDisplayNameFromInput(payload: SignUpPayload): string {
  return `${payload.firstName} ${payload.lastName}`.trim() || payload.email;
}

function sanitizeSession(s: Session): Session {
  return {
    userId: s.userId,
    email: s.email,
    displayName: s.displayName,
    token: s.token,
    expiresAt: s.expiresAt,
  };
}

/**
 * Best-effort `doesSessionExist` probe. Mobile passes the explicit
 * `supertokens-react-native` adapter via the `auth` prop; web passes
 * a `supertokens-website` / `supertokens-auth-react` adapter. Tests
 * that don't pass either fall through to the legacy
 * `require('supertokens-react-native')` discovery, which `try/catch`s
 * to `false` when the module isn't resolvable (vitest, web bundler).
 */
async function checkSuperTokensSession(
  adapter: SessionAuthAdapter | null,
): Promise<boolean> {
  if (adapter) {
    try {
      return Boolean(await adapter.doesSessionExist());
    } catch {
      return false;
    }
  }
  try {
    const mod = tryRequireSuperTokens();
    if (!mod) return false;
    const fn = mod.default?.doesSessionExist ?? mod.doesSessionExist;
    if (typeof fn !== 'function') return false;
    return Boolean(await fn());
  } catch {
    return false;
  }
}

async function runSuperTokensSignOut(
  adapter: SessionAuthAdapter | null,
): Promise<void> {
  if (adapter) {
    try {
      await adapter.signOut();
    } catch {
      // Fail open — local state has already been wiped by the caller.
    }
    return;
  }
  try {
    const mod = tryRequireSuperTokens();
    if (!mod) return;
    const fn = mod.default?.signOut ?? mod.signOut;
    if (typeof fn === 'function') await fn();
  } catch {
    // Fail open — local state has already been wiped by the caller.
  }
}

type SuperTokensModule = {
  default?: { doesSessionExist?: () => Promise<boolean>; signOut?: () => Promise<void> };
  doesSessionExist?: () => Promise<boolean>;
  signOut?: () => Promise<void>;
};

// Synchronous, statically-analysable `require` instead of `await
// import(name)` — Hermes (the JS engine on RN release builds) does
// not support dynamic `import()` with a variable specifier and bails
// the bundle compile with "Invalid expression encountered". The
// literal string lets Metro resolve the module at bundle time on
// platforms where it exists, and the try/catch keeps the call
// platform-agnostic (vitest/web fall through and return null).
function tryRequireSuperTokens(): SuperTokensModule | null {
  try {
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    return require('supertokens-react-native') as SuperTokensModule;
  } catch {
    return null;
  }
}

// Re-export so consumers that want to type their `axios` instance
// don't have to add a separate import line.
export type { AxiosInstance };
